import * as THREE from 'three';
import { Ball } from '../models/Ball';
import { eventBus } from '../events/EventBus';
import { soundManager } from '../audio/SoundManager';
import { BallSchema } from '@server/rooms/schema/BasketballRoomState';
import { Debug, LogCategory } from '../../../shared/Debug';
/**
 * Manages the basketball and its physics
 */
export class BallManager {
  private scene: THREE.Scene;
  private ball: Ball | null = null;
  private possessedBy: string | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Set up event listeners
    this.initializeEventListeners();
  }

  private initializeEventListeners(): void {
    // Listen for ball possession events
    eventBus.subscribe('ball:possession-changed', (data: { playerId: string, hasball: boolean }) => {
      this.handlePossessionChange(data.playerId, data.hasball);
    });
    
    // Listen for ball shot event
    eventBus.subscribe('ball:shot', (data: { releasePosition: THREE.Vector3, targetBasket: THREE.Vector3 }) => {
      this.handleBallShot(data.releasePosition, data.targetBasket);
    });
    
    // Listen for ball pickup request
    eventBus.subscribe('ball:pickup-requested', (data: { playerId: string }) => {
      this.possessedBy = data.playerId;
      eventBus.publish('player:ball-update', { ball: this.ball });
    });
    
    // Listen for ball release request
    eventBus.subscribe('ball:release-requested', (data: { velocity?: THREE.Vector3 }) => {
      this.releaseBall(data.velocity);
    });
    
    // Listen for ball animation changes
    eventBus.subscribe('ball:animation-changed', (data: { animation: string }) => {
      Debug.info('Ball animation changed to ' + data.animation, LogCategory.BALL);
    });
  }

  /**
   * Initialize the ball
   */
  public initialize(): void {
    // Create the ball
    this.ball = new Ball();
    this.scene.add(this.ball.getMesh());
    
    Debug.info('Ball initialized', LogCategory.BALL);
    
    // Publish event that ball is ready
    eventBus.publish('ball:initialized', this.ball);
  }


  /**
   * Update the ball based on server state - optimized for performance
   */
  public updateBall(serverBall: BallSchema, anyPlayerHasBall: boolean): void {
    if (!this.ball || !serverBall || !serverBall.dynamicBody) return;
    
    // Skip update if ball animation matches server and player has the ball
    if (anyPlayerHasBall && 
        this.ball.currentAnimation === serverBall.dynamicBody.animation) {
      return; // Don't update ball position when someone has it
    }
    
    // Check for ball bouncing (significant change in vertical velocity)
    const dynamicBody = serverBall.dynamicBody;
    
    if (!anyPlayerHasBall) {
      const prevVelocity = this.ball.getVelocity();
      const prevVelocityY = prevVelocity.y;
      const newVelocityY = dynamicBody.velocity.y;
      
      // Check for bounce with optimized condition (fewer computations)
      // A bounce occurs when velocity changes from negative to positive with significant magnitude
      // Don't play bounce sound if the ball was just passed (check horizontal velocity)
      const horizontalVelocity = Math.sqrt(
        dynamicBody.velocity.x * dynamicBody.velocity.x + 
        dynamicBody.velocity.z * dynamicBody.velocity.z
      );
      const isPass = horizontalVelocity > 5; // Threshold for pass velocity

      if (prevVelocityY < -1 && newVelocityY > 0 && !isPass) {
        // Ball bounced - calculate intensity only if needed
        const bounceIntensity = Math.min(-prevVelocityY / 10, 1);
        soundManager.play('bounce', bounceIntensity);
      }
    }
    
    // Update the ball with server data
    this.ball.updateFromServer(serverBall);
  }

  /**
   * Handle ball possession change
   */
  private handlePossessionChange(playerId: string, hasBall: boolean): void {
    if (hasBall) {
      this.possessedBy = playerId;
      // Inform PlayerManager about the ball object
      eventBus.publish('player:ball-update', { playerId, ball: this.ball, hasBall: true });
    } else {
      if (this.possessedBy === playerId) {
        this.possessedBy = null;
        // Inform PlayerManager about the ball object
        eventBus.publish('player:ball-update', { playerId, ball: null, hasBall: false });
        
        // Ensure ball is visible in the scene
        if (this.ball) {
          this.scene.add(this.ball.getMesh());
          this.ball.getMesh().visible = true;
        }
      }
    }
  }

  /**
   * Handle ball shot
   */
  private handleBallShot(releasePosition: THREE.Vector3, _: THREE.Vector3): void {
    if (!this.ball) return;
    
    // For visual purposes only - the actual physics is handled by the server
    this.scene.add(this.ball.getMesh());
    this.ball.getMesh().visible = true;
    
    // Set initial ball position from the player's position
    this.ball.setPosition(releasePosition.x, releasePosition.y, releasePosition.z);
  }

  /**
   * Release the ball with an optional velocity
   */
  private releaseBall(velocity?: THREE.Vector3): void {
    if (!this.ball) return;
    
    // Clear ball possession
    this.possessedBy = null;
    
    // Make sure ball is in the scene
    this.scene.add(this.ball.getMesh());
    this.ball.getMesh().visible = true;
    
    // Apply velocity if provided
    if (velocity) {
      this.ball.setVelocity(velocity.x, velocity.y, velocity.z);
    }
  }

  /**
   * Get the current position of the ball
   */
  public getBallPosition(): THREE.Vector3 | null {
    return this.ball ? this.ball.getPosition() : null;
  }

  /**
   * Get the ball object
   */
  public getBall(): Ball | null {
    return this.ball;
  }

  /**
   * Check if any player has the ball
   */
  public anyPlayerHasBall(): boolean {
    return this.possessedBy !== null;
  }

  /**
   * Update the ball manager (called every frame)
   * @param deltaTime Time elapsed since last update in seconds
   */
  public update(deltaTime: number): void {
    // Update the ball animation if it exists
    if (this.ball) {
      this.ball.update(deltaTime);
    }
  }

  /**
   * Cleanup resources
   */
  public dispose(): void {
    if (this.ball) {
      this.scene.remove(this.ball.getMesh());
      this.ball = null;
    }
    
    this.possessedBy = null;
  }
}